import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Flex,
  Heading,
  Button,
  Card,
  CardBody,
  CardHeader,
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Select,
  Input,
  useToast,
  Spinner,
  Center,
  Grid,
  List,
  ListItem,
  useColorModeValue,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  RadioGroup,
  Radio,
  Stack,
} from '@chakra-ui/react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import { useApp } from '../context/AppContext';
import { statisticsService } from '../services/apiService';
import HijriDatePicker from './HijriDatePicker';
import GregorianDatePicker from './GregorianDatePicker';
import { regionNames } from '../data/saudiRegions';
import { caseCategoriesService, caseTypesService } from '../services/apiService';

function SearchableSelect({
  label,
  items,
  value,
  onChange,
  isLoading,
  isDisabled,
  placeholder,
  getLabel,
  getValue,
  emptyText = 'لا توجد نتائج',
}) {
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const dropdownBg = useColorModeValue('white', 'gray.800');
  const dropdownBorder = useColorModeValue('gray.200', 'gray.600');
  const dropdownShadow = useColorModeValue('md', 'dark-lg');
  const itemHoverBg = useColorModeValue('gray.50', 'gray.700');
  const itemSelectedBg = useColorModeValue('primary.50', 'primary.900');
  const emptyTextColor = useColorModeValue('gray.500', 'gray.400');

  const selectedItem = items.find((it) => String(getValue(it)) === String(value));
  const selectedLabel = selectedItem ? getLabel(selectedItem) : '';

  const filtered = items.filter((it) =>
    (getLabel(it) || '').toLowerCase().includes((query || '').toLowerCase())
  );

  useEffect(() => {
    if (!isOpen) setQuery(selectedLabel);
  }, [value, selectedLabel, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onDocMouseDown = (e) => {
      const el = containerRef.current;
      if (!el) return;
      if (!el.contains(e.target)) {
        setIsOpen(false);
        setQuery(selectedLabel);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [isOpen, selectedLabel]);

  return (
    <FormControl isDisabled={isDisabled}>
      <FormLabel>{label}</FormLabel>
      <Box ref={containerRef} position="relative" w="100%">
        <Input
          placeholder={placeholder}
          value={isOpen ? query : (selectedLabel || '')}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!isDisabled) setIsOpen(true);
          }}
          onFocus={() => {
            if (isDisabled) return;
            setIsOpen(true);
            if (!query) setQuery('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setIsOpen(false);
              setQuery(selectedLabel);
            }
          }}
          isReadOnly={isDisabled}
          pr={isLoading ? '2.25rem' : undefined}
        />
        {isLoading ? (
          <Box position="absolute" right="0.6rem" top="50%" transform="translateY(-50%)">
            <Spinner size="sm" />
          </Box>
        ) : null}

        {isOpen && !isDisabled ? (
          <Box
            position="absolute"
            top="calc(100% + 6px)"
            left={0}
            right={0}
            bg={dropdownBg}
            border="1px solid"
            borderColor={dropdownBorder}
            borderRadius="md"
            boxShadow={dropdownShadow}
            zIndex={2000}
            p={2}
            maxH="240px"
            overflowY="auto"
          >
            {filtered.length === 0 ? (
              <Text fontSize="sm" color={emptyTextColor} p={2}>
                {emptyText}
              </Text>
            ) : (
              <List spacing={1}>
                {filtered.map((it) => {
                  const id = getValue(it);
                  const text = getLabel(it);
                  const isSelected = String(id) === String(value);
                  return (
                    <ListItem
                      key={String(id)}
                      px={3}
                      py={2}
                      borderRadius="md"
                      bg={isSelected ? itemSelectedBg : 'transparent'}
                      _hover={{ bg: itemHoverBg }}
                      cursor="pointer"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        onChange(String(id));
                        setIsOpen(false);
                        setQuery(text);
                      }}
                    >
                      <Text fontSize="sm">{text}</Text>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        ) : null}
      </Box>
    </FormControl>
  );
}

const Statistics = () => {
  const { currentUser, locations, regions, users, loadLocations, loadRegions, loadUsers } = useApp();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState(null);
  const [regionsLocationsStats, setRegionsLocationsStats] = useState(null);
  const [categoriesComparison, setCategoriesComparison] = useState(null);
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [dateType, setDateType] = useState('hijri'); // 'hijri' or 'gregorian'
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    locationId: '',
    region: '',
    userId: '',
    categoryId: '',
    typeId: '',
  });

  // Recharts renders DOM outside Chakra styling, so we style tooltip/cursor explicitly
  const chartTooltipBg = useColorModeValue('#FFFFFF', '#1A202C'); // white / gray.800
  const chartTooltipBorder = useColorModeValue('#E2E8F0', '#4A5568'); // gray.200 / gray.600
  const chartTooltipText = useColorModeValue('#1A202C', '#E2E8F0'); // gray.800 / gray.200
  const chartCursorFill = useColorModeValue('rgba(0, 0, 0, 0.04)', 'rgba(255, 255, 255, 0.10)');

  const makeTooltipContent =
    (valueFormatter) =>
    ({ active, payload, label }) => {
      if (!active || !payload || payload.length === 0) return null;

      return (
        <div
          style={{
            background: chartTooltipBg,
            border: `1px solid ${chartTooltipBorder}`,
            color: chartTooltipText,
            padding: '10px 12px',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            direction: 'rtl',
            textAlign: 'right',
            maxWidth: 280,
          }}
        >
          {label ? (
            <div style={{ fontWeight: 700, marginBottom: 6, lineHeight: 1.3 }}>{label}</div>
          ) : null}
          {payload.map((p, idx) => {
            const name = p?.name || p?.dataKey || '';
            const rawValue = p?.value;
            const value = valueFormatter ? valueFormatter(rawValue) : rawValue;
            const color = p?.color || p?.fill || '#3182CE';

            return (
              <div key={`${name}-${idx}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 3,
                      background: color,
                      display: 'inline-block',
                    }}
                  />
                  <span style={{ opacity: 0.9 }}>{name}</span>
                </div>
                <div style={{ fontWeight: 700 }}>{value}</div>
              </div>
            );
          })}
        </div>
      );
    };

  // Load initial data
  useEffect(() => {
    if (locations.length === 0) {
      loadLocations();
    }
    if (Object.keys(regions).length === 0) {
      loadRegions();
    }
    if (users.length === 0) {
      loadUsers(1, false, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load categories once for statistics filters
  useEffect(() => {
    const loadCategories = async () => {
      if (loadingCategories || categories.length > 0) return;
      setLoadingCategories(true);
      try {
        const data = await caseCategoriesService.getAll();
        setCategories(data || []);
      } catch (error) {
        console.error('Error loading categories for statistics:', error);
      } finally {
        setLoadingCategories(false);
      }
    };
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load types when category selected
  useEffect(() => {
    const loadTypes = async () => {
      if (!filters.categoryId) {
        setTypes([]);
        return;
      }
      setLoadingTypes(true);
      try {
        const data = await caseTypesService.getAll(filters.categoryId);
        setTypes(data || []);
      } catch (error) {
        console.error('Error loading types for statistics:', error);
      } finally {
        setLoadingTypes(false);
      }
    };
    loadTypes();
  }, [filters.categoryId]);

  // Load statistics on component mount
  useEffect(() => {
    loadStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper function to find user ID from input (could be ID, username, or fullName)
  const findUserId = (input) => {
    if (!input || input.trim() === '') return '';
    
    const searchValue = input.trim().toLowerCase();
    
    // First, try to find by exact ID match
    const userById = users.find(u => String(u.id) === searchValue);
    if (userById) return String(userById.id);
    
    // Then, try to find by username (case-insensitive)
    const userByUsername = users.find(u => 
      u.username && u.username.toLowerCase().includes(searchValue)
    );
    if (userByUsername) return String(userByUsername.id);
    
    // Finally, try to find by fullName (case-insensitive)
    const userByFullName = users.find(u => 
      u.fullName && u.fullName.toLowerCase().includes(searchValue)
    );
    if (userByFullName) return String(userByFullName.id);
    
    // If not found, return the input as-is (might be a valid ID that's not in the current users list)
    return input.trim();
  };

  const loadStatistics = async () => {
    setLoading(true);
    try {
      // Prepare filters with resolved userId
      const preparedFilters = { ...filters };
      if (preparedFilters.userId) {
        preparedFilters.userId = findUserId(preparedFilters.userId);
      }
      
      const [statsData, regionsLocationsData, categoriesComparisonData] = await Promise.all([
        statisticsService.getStatistics(preparedFilters),
        statisticsService.getRegionsAndLocationsStats(preparedFilters),
        statisticsService.getCategoriesComparison(preparedFilters),
      ]);
      
      setStatistics(statsData);
      setRegionsLocationsStats(regionsLocationsData);
      setCategoriesComparison(categoriesComparisonData || null);
    } catch (error) {
      console.error('Error loading statistics:', error);
      toast({
        title: 'خطأ',
        description: error.response?.data?.message || error.message || 'فشل في تحميل الإحصائيات',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleSearch = () => {
    if (filters.dateFrom && filters.dateTo && new Date(filters.dateFrom) > new Date(filters.dateTo)) {
      toast({
        title: 'خطأ في التواريخ',
        description: 'تاريخ البداية يجب أن يكون قبل أو يساوي تاريخ النهاية',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    loadStatistics();
  };

  const handleClearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      locationId: '',
      region: '',
      userId: '',
      categoryId: '',
      typeId: '',
    });
  };

  // Filter locations based on current user's role
  const getFilteredLocations = () => {
    if (!currentUser) return [];
    
    if (currentUser.role === 'System Administrator') {
      return locations;
    } else if (currentUser.role === 'Region Supervisor') {
      return locations.filter(loc => loc.region === currentUser.region);
    } else if (currentUser.role === 'Location Supervisor') {
      return locations.filter(loc => String(loc.id) === String(currentUser.locationId || currentUser.location_id));
    }
    return [];
  };

  // Get unique regions based on current user's role
  const getFilteredRegions = () => {
    if (!currentUser) return [];
    
    if (currentUser.role === 'System Administrator') {
      // Use regionNames directly from saudiRegions.js (array of region names)
      return regionNames || [];
    } else if (currentUser.role === 'Region Supervisor') {
      return currentUser.region ? [currentUser.region] : [];
    }
    return [];
  };

  const hasActiveFilters = filters.dateFrom || filters.dateTo || filters.locationId || filters.region || filters.userId || filters.categoryId || filters.typeId;

  return (
    <Box>
      <VStack align="stretch" spacing={6}>
        <Flex justify="space-between" align="center">
          <Heading size="lg">الإحصائيات</Heading>
        </Flex>

        {/* Filters Card */}
        <Card>
          <CardHeader>
            <Heading size="md">الفلاتر</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              {/* Date Type Radio Buttons */}
              <FormControl>
                <FormLabel>نوع التاريخ</FormLabel>
                <RadioGroup value={dateType} onChange={setDateType}>
                  <Stack direction="row" spacing={4}>
                    <Radio value="hijri" colorScheme="primary">
                      هجري
                    </Radio>
                    <Radio value="gregorian" colorScheme="primary">
                      ميلادي
                    </Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>

              <Flex direction={{ base: 'column', md: 'row' }} gap={4}>
                <FormControl>
                  <FormLabel>تاريخ الإنشاء من</FormLabel>
                  {dateType === 'hijri' ? (
                    <HijriDatePicker
                      value={filters.dateFrom}
                      onChange={(date) => handleFilterChange('dateFrom', date)}
                      placeholder="اختر تاريخ البداية"
                    />
                  ) : (
                    <GregorianDatePicker
                      value={filters.dateFrom}
                      onChange={(date) => handleFilterChange('dateFrom', date)}
                      placeholder="اختر تاريخ البداية"
                    />
                  )}
                </FormControl>
                <FormControl>
                  <FormLabel>تاريخ الإنشاء إلى</FormLabel>
                  {dateType === 'hijri' ? (
                    <HijriDatePicker
                      value={filters.dateTo}
                      onChange={(date) => handleFilterChange('dateTo', date)}
                      placeholder="اختر تاريخ النهاية"
                    />
                  ) : (
                    <GregorianDatePicker
                      value={filters.dateTo}
                      onChange={(date) => handleFilterChange('dateTo', date)}
                      placeholder="اختر تاريخ النهاية"
                    />
                  )}
                </FormControl>
              </Flex>

              <Flex direction={{ base: 'column', md: 'row' }} gap={4}>
                {getFilteredRegions().length > 0 && (
                  <FormControl>
                    <FormLabel>المنطقة</FormLabel>
                    <Select
                      value={filters.region}
                      onChange={(e) => handleFilterChange('region', e.target.value)}
                      placeholder="اختر المنطقة"
                    >
                      <option value="">الكل</option>
                      {getFilteredRegions().map((region) => (
                        <option key={region} value={region}>
                          {region}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {getFilteredLocations().length > 0 && (
                  <FormControl>
                    <FormLabel>الموقع</FormLabel>
                    <Select
                      value={filters.locationId}
                      onChange={(e) => handleFilterChange('locationId', e.target.value)}
                      placeholder="اختر الموقع"
                    >
                      <option value="">الكل</option>
                      {getFilteredLocations().map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.name} ({location.region} - {location.city})
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <FormControl>
                  <FormLabel>المستخدم (رقم المستخدم أو الاسم)</FormLabel>
                  <Input
                    type="text"
                    value={filters.userId}
                    onChange={(e) => handleFilterChange('userId', e.target.value)}
                    placeholder="أدخل رقم المستخدم أو الاسم"
                  />
                </FormControl>
              </Flex>

              <Flex direction={{ base: 'column', md: 'row' }} gap={4}>
                <SearchableSelect
                  label="صنف القضية"
                  items={categories}
                  value={filters.categoryId}
                  isLoading={loadingCategories}
                  isDisabled={loading || loadingCategories || loadingTypes}
                  placeholder="اختر/ابحث عن الصنف"
                  getLabel={(c) => c.name}
                  getValue={(c) => c.id}
                  onChange={(newCategoryId) => {
                    setFilters((prev) => ({
                      ...prev,
                      categoryId: newCategoryId,
                      typeId: '',
                    }));
                  }}
                />

                <SearchableSelect
                  label="نوع القضية"
                  items={types}
                  value={filters.typeId}
                  isLoading={loadingTypes}
                  isDisabled={loading || !filters.categoryId || loadingTypes}
                  placeholder={filters.categoryId ? 'اختر/ابحث عن النوع' : 'اختر الصنف أولاً'}
                  getLabel={(t) => t.name}
                  getValue={(t) => t.id}
                  onChange={(newTypeId) => {
                    setFilters((prev) => ({ ...prev, typeId: newTypeId }));
                  }}
                />
              </Flex>

              <HStack spacing={2}>
                <Button colorScheme="primary" onClick={handleSearch} isLoading={loading}>
                  بحث
                </Button>
                {hasActiveFilters && (
                  <Button variant="ghost" colorScheme="red" onClick={handleClearFilters}>
                    مسح الفلاتر
                  </Button>
                )}
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Statistics Cards */}
        {loading ? (
          <Center py={10}>
            <VStack spacing={4}>
              <Spinner size="xl" color="primary.500" thickness="4px" speed="0.65s" />
              <Text color="gray.600">جاري تحميل الإحصائيات...</Text>
            </VStack>
          </Center>
        ) : statistics ? (
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={6}>
            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>عدد القضايا</StatLabel>
                  <StatNumber color="primary.600">{statistics.casesCount || 0}</StatNumber>
                  <StatHelpText>إجمالي القضايا</StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>عدد العينات</StatLabel>
                  <StatNumber color="blue.600">{statistics.samplesCount || 0}</StatNumber>
                  <StatHelpText>إجمالي العينات</StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>عدد الآثار</StatLabel>
                  <StatNumber color="green.600">{statistics.tracesCount || 0}</StatNumber>
                  <StatHelpText>إجمالي الآثار</StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>الآثار الصالحة</StatLabel>
                  <StatNumber color="green.600">
                    {statistics.validTracesCount || 0}
                  </StatNumber>
                  <StatHelpText>
                    {statistics.tracesCount > 0
                      ? `${Math.round((statistics.validTracesCount / statistics.tracesCount) * 100)}% من إجمالي الآثار`
                      : '0%'}
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>الآثار غير الصالحة</StatLabel>
                  <StatNumber color="red.600">
                    {statistics.invalidTracesCount || 0}
                  </StatNumber>
                  <StatHelpText>
                    {statistics.tracesCount > 0
                      ? `${Math.round((statistics.invalidTracesCount / statistics.tracesCount) * 100)}% من إجمالي الآثار`
                      : '0%'}
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>الآثار المتطابقة</StatLabel>
                  <StatNumber color="green.600">
                    {statistics.matchTracesCount || 0}
                  </StatNumber>
                  <StatHelpText>
                    {statistics.validTracesCount > 0
                      ? `${Math.round((statistics.matchTracesCount / statistics.validTracesCount) * 100)}% من الآثار الصالحة`
                      : '0%'}
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>الآثار غير المتطابقة</StatLabel>
                  <StatNumber color="orange.600">
                    {statistics.mismatchTracesCount || 0}
                  </StatNumber>
                  <StatHelpText>
                    {statistics.validTracesCount > 0
                      ? `${Math.round((statistics.mismatchTracesCount / statistics.validTracesCount) * 100)}% من الآثار الصالحة`
                      : '0%'}
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </Grid>
        ) : (
          <Card>
            <CardBody>
              <Text textAlign="center" color="gray.500">
                لا توجد إحصائيات متاحة
              </Text>
            </CardBody>
          </Card>
        )}

        {/* Charts Section */}
        {statistics && !loading && (
          <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={6}>
            {/* Pie Chart - Valid vs Invalid Traces */}
            <Card>
              <CardHeader>
                <Heading size="md">توزيع الآثار (صالح / غير صالح)</Heading>
              </CardHeader>
              <CardBody>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'صالح', value: statistics.validTracesCount || 0, color: '#38A169' },
                        { name: 'غير صالح', value: statistics.invalidTracesCount || 0, color: '#E53E3E' },
                      ]}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {[
                        { name: 'صالح', value: statistics.validTracesCount || 0, color: '#38A169' },
                        { name: 'غير صالح', value: statistics.invalidTracesCount || 0, color: '#E53E3E' },
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={makeTooltipContent()} cursor={{ fill: chartCursorFill }} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardBody>
            </Card>

            {/* Pie Chart - Match vs Mismatch Traces */}
            <Card>
              <CardHeader>
                <Heading size="md">توزيع الآثار الصالحة (متطابق / غير متطابق)</Heading>
              </CardHeader>
              <CardBody>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'متطابق', value: statistics.matchTracesCount || 0, color: '#D69E2E' },
                        { name: 'غير متطابق', value: statistics.mismatchTracesCount || 0, color: '#DD6B20' },
                      ]}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {[
                        { name: 'متطابق', value: statistics.matchTracesCount || 0, color: '#D69E2E' },
                        { name: 'غير متطابق', value: statistics.mismatchTracesCount || 0, color: '#DD6B20' },
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={makeTooltipContent()} cursor={{ fill: chartCursorFill }} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardBody>
            </Card>

            {/* Bar Chart - Cases, Samples, Traces Comparison */}
            <Card>
              <CardHeader>
                <Heading size="md">مقارنة إجمالي القضايا والعينات والآثار</Heading>
              </CardHeader>
              <CardBody>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={[
                      {
                        name: 'القضايا',
                        value: statistics.casesCount || 0,
                      },
                      {
                        name: 'العينات',
                        value: statistics.samplesCount || 0,
                      },
                      {
                        name: 'الآثار',
                        value: statistics.tracesCount || 0,
                      },
                    ]}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip content={makeTooltipContent()} cursor={{ fill: chartCursorFill }} />
                    <Legend />
                    <Bar dataKey="value" fill="#3182CE" fillOpacity={1} activeBar={false} name="العدد" />
                  </BarChart>
                </ResponsiveContainer>
              </CardBody>
            </Card>

            {/* Bar Chart - Traces Status Distribution */}
            <Card>
              <CardHeader>
                <Heading size="md">توزيع الآثار حسب الحالة</Heading>
              </CardHeader>
              <CardBody>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={[
                      {
                        name: 'صالح',
                        value: statistics.validTracesCount || 0,
                        color: '#38A169',
                      },
                      {
                        name: 'غير صالح',
                        value: statistics.invalidTracesCount || 0,
                        color: '#E53E3E',
                      },
                      {
                        name: 'متطابق',
                        value: statistics.matchTracesCount || 0,
                        color: '#D69E2E',
                      },
                      {
                        name: 'غير متطابق',
                        value: statistics.mismatchTracesCount || 0,
                        color: '#DD6B20',
                      },
                    ]}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip content={makeTooltipContent()} cursor={{ fill: chartCursorFill }} />
                    <Legend />
                    <Bar dataKey="value" name="العدد" fillOpacity={1} activeBar={false}>
                      {[
                        { name: 'صالح', value: statistics.validTracesCount || 0, color: '#38A169' },
                        { name: 'غير صالح', value: statistics.invalidTracesCount || 0, color: '#E53E3E' },
                        { name: 'متطابق', value: statistics.matchTracesCount || 0, color: '#D69E2E' },
                        { name: 'غير متطابق', value: statistics.mismatchTracesCount || 0, color: '#DD6B20' },
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardBody>
            </Card>

            {/* Line Chart - Percentages Comparison */}
            <Card gridColumn={{ base: '1', lg: '1 / -1' }}>
              <CardHeader>
                <Heading size="md">مقارنة النسب المئوية</Heading>
              </CardHeader>
              <CardBody>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={[
                      {
                        name: 'نسبة الصالح',
                        value: statistics.tracesCount > 0
                          ? Math.round((statistics.validTracesCount / statistics.tracesCount) * 100)
                          : 0,
                      },
                      {
                        name: 'نسبة غير الصالح',
                        value: statistics.tracesCount > 0
                          ? Math.round((statistics.invalidTracesCount / statistics.tracesCount) * 100)
                          : 0,
                      },
                      {
                        name: 'نسبة المتطابق',
                        value: statistics.validTracesCount > 0
                          ? Math.round((statistics.matchTracesCount / statistics.validTracesCount) * 100)
                          : 0,
                      },
                      {
                        name: 'نسبة غير المتطابق',
                        value: statistics.validTracesCount > 0
                          ? Math.round((statistics.mismatchTracesCount / statistics.validTracesCount) * 100)
                          : 0,
                      },
                    ]}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis domain={[0, 100]} />
                    <Tooltip content={makeTooltipContent((v) => `${v}%`)} cursor={{ fill: chartCursorFill }} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#3182CE"
                      strokeWidth={3}
                      name="النسبة المئوية (%)"
                      dot={{ r: 6 }}
                      activeDot={{ r: 8 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardBody>
            </Card>

            {/* Summary Comparison Card */}
            <Card gridColumn={{ base: '1', lg: '1 / -1' }}>
              <CardHeader>
                <Heading size="md">ملخص المقارنات</Heading>
              </CardHeader>
              <CardBody>
                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={4}>
                  <Box
                    p={4}
                    borderRadius="md"
                    bg="green.50"
                    border="1px solid"
                    borderColor="green.200"
                  >
                    <Text fontSize="sm" color="gray.600" mb={1}>
                      معدل الآثار الصالحة
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold" color="green.600">
                      {statistics.tracesCount > 0
                        ? `${Math.round((statistics.validTracesCount / statistics.tracesCount) * 100)}%`
                        : '0%'}
                    </Text>
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      {statistics.validTracesCount || 0} من {statistics.tracesCount || 0}
                    </Text>
                  </Box>

                  <Box
                    p={4}
                    borderRadius="md"
                    bg="red.50"
                    border="1px solid"
                    borderColor="red.200"
                  >
                    <Text fontSize="sm" color="gray.600" mb={1}>
                      معدل الآثار غير الصالحة
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold" color="red.600">
                      {statistics.tracesCount > 0
                        ? `${Math.round((statistics.invalidTracesCount / statistics.tracesCount) * 100)}%`
                        : '0%'}
                    </Text>
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      {statistics.invalidTracesCount || 0} من {statistics.tracesCount || 0}
                    </Text>
                  </Box>

                  <Box
                    p={4}
                    borderRadius="md"
                    bg="yellow.50"
                    border="1px solid"
                    borderColor="yellow.200"
                  >
                    <Text fontSize="sm" color="gray.600" mb={1}>
                      معدل التطابق
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold" color="yellow.600">
                      {statistics.validTracesCount > 0
                        ? `${Math.round((statistics.matchTracesCount / statistics.validTracesCount) * 100)}%`
                        : '0%'}
                    </Text>
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      {statistics.matchTracesCount || 0} من {statistics.validTracesCount || 0}
                    </Text>
                  </Box>

                  <Box
                    p={4}
                    borderRadius="md"
                    bg="orange.50"
                    border="1px solid"
                    borderColor="orange.200"
                  >
                    <Text fontSize="sm" color="gray.600" mb={1}>
                      معدل عدم التطابق
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold" color="orange.600">
                      {statistics.validTracesCount > 0
                        ? `${Math.round((statistics.mismatchTracesCount / statistics.validTracesCount) * 100)}%`
                        : '0%'}
                    </Text>
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      {statistics.mismatchTracesCount || 0} من {statistics.validTracesCount || 0}
                    </Text>
                  </Box>
                </Grid>
              </CardBody>
            </Card>
          </Grid>
        )}

        {/* Regions and Locations Comparison Charts */}
        {regionsLocationsStats && !loading && (
          <Grid templateColumns={{ base: '1fr' }} gap={6}>
            {/* Category comparison (top category per region OR distribution inside selected region) */}
            {categoriesComparison && categoriesComparison.items && categoriesComparison.items.length > 0 && (
              <Card>
                <CardHeader>
                  <Heading size="md">
                    {filters.region
                      ? `توزيع أصناف القضايا في ${filters.region}`
                      : 'أكثر صنف قضايا لكل منطقة'}
                  </Heading>
                </CardHeader>
                <CardBody>
                  {!filters.region ? (
                    <VStack align="stretch" spacing={2}>
                      {categoriesComparison.items.map((row) => (
                        <Flex key={row.region} justify="space-between" align="center">
                          <Text fontWeight="semibold">{row.region}</Text>
                          <Text>
                            {row.category?.name || 'غير محدد'} ({row.casesCount})
                          </Text>
                        </Flex>
                      ))}
                    </VStack>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={categoriesComparison.items.map((r) => ({
                          name: r.category?.name || 'غير محدد',
                          casesCount: r.casesCount,
                        }))}
                        margin={{ top: 20, right: 5, left: 5, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" hide />
                        <YAxis />
                        <Tooltip content={makeTooltipContent((v) => `${v} قضية`)} cursor={{ fill: chartCursorFill }} />
                        <Bar dataKey="casesCount" name="عدد القضايا" fill="#3182CE" fillOpacity={1} activeBar={false} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardBody>
              </Card>
            )}

            {/* Regions Comparison - Only show if no region filter is selected */}
            {!filters.region && !filters.locationId && regionsLocationsStats.regions && regionsLocationsStats.regions.length > 0 && (
              <Card>
                <CardHeader>
                  <Heading size="md">مقارنة عدد القضايا حسب المناطق</Heading>
                </CardHeader>
                <CardBody>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={regionsLocationsStats.regions.map((region, index) => ({
                        name: region.name,
                        casesCount: region.casesCount,
                        index: index,
                      }))}
                      margin={{ top: 20, right: 5, left: 5, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip content={makeTooltipContent((v) => `${v} قضية`)} cursor={{ fill: chartCursorFill }} />
                      <Bar dataKey="casesCount" name="عدد القضايا" fillOpacity={1} activeBar={false}>
                        {regionsLocationsStats.regions.map((region, index) => {
                          const colors = ['#3182CE', '#10b981', '#D69E2E', '#DD6B20', '#E53E3E', '#805AD5', '#38A169', '#F6AD55'];
                          const color = colors[index % colors.length];
                          return <Cell key={`cell-${index}`} fill={color} />;
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>
            )}

            {/* Locations Comparison - Only show if no location filter is selected */}
            {!filters.locationId && regionsLocationsStats.locations && regionsLocationsStats.locations.length > 0 && (
              <Card>
                <CardHeader>
                  <Heading size="md">
                    {filters.region 
                      ? `مقارنة عدد القضايا حسب المواقع في ${filters.region}`
                      : 'مقارنة عدد القضايا حسب المواقع'}
                  </Heading>
                </CardHeader>
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={regionsLocationsStats.locations
                          .filter(loc => !filters.region || loc.region === filters.region)
                          .map((location, index) => ({
                            name: location.name,
                            casesCount: location.casesCount,
                            index: index,
                          }))}
                        margin={{ top: 20, right: 5, left: 5, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" hide />
                        <YAxis />
                        <Tooltip content={makeTooltipContent((v) => `${v} قضية`)} cursor={{ fill: chartCursorFill }} />
                        <Bar dataKey="casesCount" name="عدد القضايا" fillOpacity={1} activeBar={false}>
                          {regionsLocationsStats.locations
                            .filter(loc => !filters.region || loc.region === filters.region)
                            .map((location, index) => {
                              const colors = ['#3182CE', '#10b981', '#D69E2E', '#DD6B20', '#E53E3E', '#805AD5', '#38A169', '#F6AD55', '#ED64A6', '#48BB78', '#F56565', '#ED8936'];
                              const color = colors[index % colors.length];
                              return <Cell key={`cell-${index}`} fill={color} />;
                            })}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <Grid templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }} gap={3}>
                      {regionsLocationsStats.locations
                        .filter(loc => !filters.region || loc.region === filters.region)
                        .map((location, index) => {
                          const colors = ['#3182CE', '#10b981', '#D69E2E', '#DD6B20', '#E53E3E', '#805AD5', '#38A169', '#F6AD55', '#ED64A6', '#48BB78', '#F56565', '#ED8936'];
                          const color = colors[index % colors.length];
                          return (
                            <HStack key={location.id} spacing={2}>
                              <Box w={4} h={4} bg={color} borderRadius="sm" flexShrink={0} />
                              <Text fontSize="sm" fontWeight="medium" isTruncated title={location.name}>
                                {location.name}
                              </Text>
                            </HStack>
                          );
                        })}
                    </Grid>
                  </VStack>
                </CardBody>
              </Card>
            )}
          </Grid>
        )}

      </VStack>
    </Box>
  );
};

export default Statistics;

